import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { uniqBy } from "lodash-es";
import { defineMessages, useIntl } from "react-intl";
import { useHistory } from "react-router-dom";

import { GuideStatus } from "@mapmycustomers/shared";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import Checklist, { CompletionStatus } from "@app/component/flows/base/Checklist";
import ChecklistItemType from "@app/component/flows/base/Checklist/types/ChecklistItemType";
import CloseConfirmationModal from "@app/component/flows/base/CloseConfirmationModal";
import ChecklistDescriptor, {
  ChecklistStatus,
  ChecklistStep,
} from "@app/component/flows/types/ChecklistDescriptor";
import TourDescriptor, {
  TourStep,
  TourStepsStatus,
} from "@app/component/flows/types/TourDescriptor";
import getNextGuideStatus from "@app/component/flows/utils/getNextGuideStatus";
import { getFlowStatuses, getMe } from "@app/store/iam";
import { updateFlowStatus } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import useGuides from "@app/util/contexts/useGuides";

import Tour from "./MmcTour";

const messages = defineMessages({
  completionStatus: {
    id: "guides.checklistRunner.item.info.tourCompletion",
    defaultMessage: "{complete}/{total} {total, plural, one {step} other {steps}} complete",
  },
});

export interface ChecklistRunnerProps {
  descriptor: ChecklistDescriptor;
  onHideConfirm?: (closeMode: "close" | "forever") => void;
  onTourShow?: (slug: TourDescriptor["id"]) => void;
}

const ChecklistRunner = ({
  descriptor,
  me,
  onHideConfirm,
  onTourShow,
  onUpdateFlowStatus,
  status,
}: ChecklistRunnerProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const history = useHistory();
  const { getCompletionPath, getGuideStatus, hideGuide, updateGuideStatus, visibility } =
    useGuides();

  const currentUserRole = me.role;

  const handleStatusChange = useCallback(
    (status: GuideStatus, force?: boolean) => {
      const guideStatus = getGuideStatus(descriptor.id);
      if (guideStatus) {
        const newGuideStatus = force ? status : getNextGuideStatus(guideStatus.status, status);
        if (newGuideStatus !== guideStatus.status) {
          updateGuideStatus({ ...guideStatus, status: newGuideStatus });
        }
      } else {
        console.log("tours: guide status not found for slug", descriptor.id);
      }
    },
    [descriptor.id, getGuideStatus, updateGuideStatus]
  );

  const [closeConfirmationVisible, setCloseConfirmationModalVisibility] = useState(false);

  const handleHide = useCallback(() => {
    if (onHideConfirm) {
      setCloseConfirmationModalVisibility(true);
    } else {
      hideGuide(descriptor.id);
    }
  }, [hideGuide, descriptor.id, onHideConfirm]);

  const items: ChecklistItemType[] = useMemo(() => {
    return descriptor.steps
      .filter(
        (step) =>
          (step?.eligibleRoles?.includes(currentUserRole.key) ?? true) &&
          (step.isVisible?.(descriptor.id, step.id, me) ?? true)
      )
      .map((step) => {
        let info: ReactNode | undefined = undefined;
        const eligibleTourSteps =
          step.tour?.steps.filter(
            ({ eligibleRoles }) => eligibleRoles?.includes(currentUserRole.key) ?? true
          ) ?? [];
        if (eligibleTourSteps.length) {
          const eligibleStepsCount = uniqBy(
            eligibleTourSteps.filter((step) => step.description),
            (step) => step.key ?? step.id
          ).length;
          const numberOfCompletedSteps = !status?.steps?.[step.id]
            ? 0
            : status.steps[step.id] === true
              ? eligibleStepsCount
              : Object.keys((status.steps[step.id] as TourStepsStatus) ?? {}).length;
          info = intl.formatMessage(messages.completionStatus, {
            complete: numberOfCompletedSteps,
            total: eligibleStepsCount,
          });
        }
        return { id: step.id, info, title: intl.formatMessage(step.title) };
      });
  }, [descriptor.steps, descriptor.id, currentUserRole.key, intl, status?.steps, me]);

  const completionStatus = useMemo(() => {
    return descriptor.steps.reduce((result, step) => {
      if (status?.steps?.[step.id] === true) {
        return { ...result, [step.id]: true };
      }
      const isComplete = !step.tour
        ? false // otherwise it should be set to true as mentioned above
        : !!status?.steps?.[step.id] &&
          step.tour.steps
            .filter(({ eligibleRoles }) => eligibleRoles?.includes(currentUserRole.key) ?? true)
            .every((tourStep) => (status?.steps[step.id] as TourStepsStatus)?.[tourStep.id]);
      return { ...result, [step.id]: isComplete };
    }, {} as CompletionStatus);
  }, [status, descriptor.steps, currentUserRole.key]);

  const [activeStep, setActiveStep] = useState<ChecklistStep | undefined>();

  const handleItemClick = useCallback(
    (slug: ChecklistDescriptor["id"], itemId: ChecklistStep["id"]) => {
      const step = descriptor.steps.find(({ id }) => id === itemId);
      if (!step) {
        return;
      }
      if (
        step.path &&
        ((window.location.pathname !== step.path && step.pathStrict) ||
          !window.location.pathname.startsWith(step.path))
      ) {
        history.push(step.path);
      }
      if (step.tour) {
        onTourShow?.(step.tour.id);
      }
      step.onClick?.(descriptor.id, step.id);
      setActiveStep(step);
    },
    [descriptor.id, descriptor.steps, history, onTourShow]
  );

  const handleTourHide = useCallback(() => {
    if (onHideConfirm) {
      setCloseConfirmationModalVisibility(true);
    } else {
      setActiveStep(undefined);
    }
  }, [onHideConfirm]);

  const handleTourComplete = useDynamicCallback(() => {
    setActiveStep((activeStep) => {
      if (!activeStep) {
        return undefined;
      }

      const newStatus: ChecklistStatus = {
        ...(status ?? {}),
        steps: { ...(status?.steps ?? {}), [activeStep.id]: true },
      };
      onUpdateFlowStatus({ slug: descriptor.id, status: newStatus });
      const checklistDone = descriptor.steps
        .filter(
          (step) =>
            (step.eligibleRoles?.includes(currentUserRole.key) ?? true) &&
            (step.isVisible?.(descriptor.id, step.id, me) ?? true)
        )
        .every((step) => newStatus.steps[step.id] === true);
      if (checklistDone) {
        hideGuide(descriptor.id);
        handleStatusChange(GuideStatus.COMPLETED);
        if (descriptor.completionPath) {
          history.push(descriptor.completionPath);
        } else {
          const path = getCompletionPath(descriptor.id);
          if (path) {
            history.push(path);
          }
        }
      }
      return undefined;
    });
  });

  const handleStepComplete = useCallback(
    (id: TourDescriptor["id"], step: TourStep) => {
      if (!activeStep) {
        return;
      }
      const newStatus: ChecklistStatus = {
        ...(status ?? {}),
        steps: {
          ...(status?.steps ?? {}),
          [activeStep.id]:
            // if the entire tour is complete, we don't reset its status keeping it "complete"
            status?.steps?.[activeStep.id] === true
              ? true
              : {
                  ...(status?.steps?.[activeStep.id] as TourStepsStatus),
                  [step.key ?? step.id]: true,
                },
        },
      };
      onUpdateFlowStatus({ slug: descriptor.id, status: newStatus });
    },
    [activeStep, status, onUpdateFlowStatus, descriptor.id]
  );

  const handleConfirmedHide = useCallback(
    (closeMode: "cancel" | "close" | "forever") => {
      setCloseConfirmationModalVisibility(false);
      if (closeMode === "cancel") {
        return;
      }
      setActiveStep(undefined);
      hideGuide(descriptor.id);
      onHideConfirm?.(closeMode);
    },
    [descriptor.id, hideGuide, onHideConfirm]
  );

  useEffect(() => {
    if (!visibility[descriptor.id]) {
      setActiveStep(undefined);
    }
  }, [descriptor.id, visibility]);

  return (
    <>
      {visibility[descriptor.id] && !activeStep ? (
        <Checklist
          completionStatus={completionStatus}
          id={descriptor.id}
          items={items}
          onHide={handleHide}
          onItemClick={handleItemClick}
          onStatusChange={handleStatusChange}
          subTitle={descriptor.subTitle ? intl.formatMessage(descriptor.subTitle) : undefined}
          title={intl.formatMessage(descriptor.title)}
          visible
        />
      ) : null}
      {activeStep?.tour && (
        <Tour
          descriptor={activeStep.tour}
          onComplete={handleTourComplete}
          onHide={handleTourHide}
          onStepComplete={handleStepComplete}
          visible
        />
      )}
      {closeConfirmationVisible && <CloseConfirmationModal onHide={handleConfirmedHide} />}
    </>
  );
};

const mapStateToProps = (state: RootState, { descriptor }: ChecklistRunnerProps) => ({
  me: getMe(state)!,
  status: getFlowStatuses(state)[descriptor.id] as ChecklistStatus | undefined,
});

const mapDispatchToProps = {
  onUpdateFlowStatus: updateFlowStatus.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChecklistRunner);
